import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface ConfirmContextValue {
  /** Returns a promise that resolves to true if the user confirmed, false otherwise. */
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

interface PendingConfirm extends ConfirmOptions {
  resolve: (v: boolean) => void;
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...opts, resolve });
      }),
    [],
  );

  // Focus the confirm button when the dialog opens (keyboard accessibility)
  useEffect(() => {
    if (pending) {
      const t = setTimeout(() => confirmButtonRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [pending]);

  // ESC closes (cancel)
  useEffect(() => {
    const current = pending;
    if (!current) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        current!.resolve(false);
        setPending(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending]);

  function handleResolve(value: boolean) {
    if (pending) {
      pending.resolve(value);
      setPending(null);
    }
  }

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          aria-describedby={pending.description ? 'confirm-dialog-description' : undefined}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleResolve(false);
          }}
        >
          <div className="card max-h-[85vh] w-full max-w-md overflow-y-auto p-6">
            <h2 id="confirm-dialog-title" className="break-words text-lg font-semibold text-slate-900">
              {pending.title}
            </h2>
            {pending.description && (
              // break-words: descriptions interpolate user-typed titles (ticket
              // names, conversation titles) that can contain long unbroken
              // tokens which would otherwise spill out of the dialog.
              <p id="confirm-dialog-description" className="mt-2 break-words text-sm text-slate-600">
                {pending.description}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => handleResolve(false)} className="btn-ghost">
                {pending.cancelLabel ?? 'Cancel'}
              </button>
              <button
                ref={confirmButtonRef}
                type="button"
                onClick={() => handleResolve(true)}
                className={
                  pending.destructive
                    ? 'btn inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 min-h-[44px]'
                    : 'btn-primary'
                }
              >
                {pending.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmDialogProvider>');
  return ctx;
}
